import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { useSelector } from 'react-redux';

import { syncPostHogIdentity } from '@lib/analytics/posthogIdentity';
import { useAuth } from '@lib/auth';
import { isPostHogLoaded, onPostHogLoaded, posthog } from '@lib/posthog.lib';
import type { RootState } from '@lib/redux.lib/store';

/**
 * Enables PostHog pageviews, pageleave, and session recording only after
 * auth bootstrap finishes so anonymous/stale identities are not captured.
 *
 * Identity sync is separate from route listeners: a profile id/email change
 * starts a fresh recording for the new user without re-binding router events.
 * Middleware may have skipped identify/reset while `posthog.__loaded` was
 * still false, so this also re-syncs once the SDK is ready.
 */
export const PostHogAnalyticsLifecycle = (): null => {
  const router = useRouter();
  const { loading: isAuthLoading } = useAuth();
  const profileId = useSelector(
    (state: RootState) => state.app.user.profile?.id
  );
  const profileEmail = useSelector(
    (state: RootState) => state.app.user.profile?.email
  );
  const [isPostHogReady, setIsPostHogReady] = useState(isPostHogLoaded);

  useEffect(() => {
    if (isPostHogReady) {
      return;
    }

    return onPostHogLoaded(() => {
      setIsPostHogReady(true);
    });
  }, [isPostHogReady]);

  // Route listeners stay mounted for the authenticated session; do not depend
  // on profile fields or each identity change would tear them down/rebind.
  useEffect(() => {
    if (isAuthLoading || !isPostHogReady) {
      return;
    }

    posthog.set_config({
      capture_pageleave: true,
      disable_session_recording: false,
    });

    const handleRouteChange = () => {
      posthog.capture('$pageview');
    };

    router.events.on('routeChangeComplete', handleRouteChange);

    return () => {
      router.events.off('routeChangeComplete', handleRouteChange);
    };
  }, [isAuthLoading, isPostHogReady, router.events]);

  // Identity + recording: id or email change means another user session, so
  // restart recording and attribute the current page to the new identity.
  useEffect(() => {
    if (isAuthLoading || !isPostHogReady) {
      return;
    }

    syncPostHogIdentity(profileId, profileEmail);

    if (profileId == null) {
      posthog.stopSessionRecording();

      return;
    }

    posthog.set_config({
      capture_pageleave: true,
      disable_session_recording: false,
    });
    posthog.stopSessionRecording();
    posthog.startSessionRecording();
    posthog.capture('$pageview');
  }, [isAuthLoading, isPostHogReady, profileId, profileEmail]);

  return null;
};
